import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  ACTION_VARIANTS,
  Button,
  Icon,
  ICON_NAMES,
  Link,
} from '@reverse-hr/design-system';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { showcaseTableSelector } from '@app/store/selectors/showcase-table';
import { selectorUser } from '@model/User';
import { USER_TYPES_MAP } from '@app/constants/session';
import { useParams } from 'react-router-dom';

const TableHeader = ({ headerGroups, columns }) => {
  const { columns: storeColumns } = useStoreState(showcaseTableSelector);
  const { type: userType } = useStoreState(selectorUser);

  const { setColumnsOrder, hideColumn } = useStoreActions(
    showcaseTableSelector,
  );

  const { entityId: jobId } = useParams();

  const isUserManager = userType === USER_TYPES_MAP.MANAGER;

  const handleDragEnd = event => {
    const startPosition = event.source.index;
    const landingPosition = event?.destination?.index;
    if (!startPosition || !landingPosition) return;

    setColumnsOrder({
      startPositionId: columns[startPosition]?.id,
      landingPositionId: columns[landingPosition]?.id,
    });
  };

  return (
    <thead>
      {headerGroups.map((headerGroup, headerGroupIndex) => (
        <DragDropContext onDragEnd={handleDragEnd} key={headerGroupIndex}>
          <Droppable droppableId="droppable" direction="horizontal">
            {droppableProvided => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                style={{}}
                ref={droppableProvided.innerRef}
                className="c-app-showcase__row c-app-showcase__row--first"
              >
                {headerGroup.headers.map((column, index) =>
                  column.id === 'initial-column' ? (
                    <th
                      {...column.getHeaderProps()}
                      style={{}}
                      className="c-app-showcase__label c-app-showcase__label--first"
                      key={column.id}
                    ></th>
                  ) : (
                    <Draggable
                      key={column.id}
                      draggableId={column.id}
                      index={index}
                      isDragDisabled={
                        !column.accessor || column.id === 'initial-column'
                      }
                    >
                      {provided => {
                        let className = 'c-app-showcase__cell';

                        const storeColumn = storeColumns?.find(
                          storeColumn => storeColumn.id === column.id,
                        );

                        const editLinkURL = `${__ENVIRONMENT_CONFIG__.API_HOST}/dashboard/recruitment/jobs/${jobId}/candidates/${storeColumn.candidateId}/edit`;

                        if (storeColumn?.isInterestedBut) {
                          className += ' c-app-showcase__cell--interested-but';
                        }

                        if (storeColumn?.isDiscarded) {
                          className += ' c-app-showcase__cell--out';
                        }

                        const { key, ...thProps } = {
                          ...column.getHeaderProps(),
                          ...provided.draggableProps,
                          ...provided.dragHandleProps,
                        };

                        return (
                          <th
                            key={key}
                            {...thProps}
                            className={className}
                            ref={provided.innerRef}
                            style={{ ...provided.draggableProps.style }}
                            data-testid="showcase-page-application"
                          >
                            <div className="rev-o-antipodes">
                              <div className="rev-o-antipodes__item c-app-showcase__drag">
                                <Icon
                                  name={ICON_NAMES.GRID_VERTICAL}
                                  size={20}
                                />
                              </div>

                              {isUserManager && (
                                <div className="rev-o-antipodes__item">
                                  <Link
                                    className="c-app-showcase__edit"
                                    href={editLinkURL}
                                    leftIcon={ICON_NAMES.EDIT}
                                    target="_blank"
                                    variant={ACTION_VARIANTS.GHOST}
                                  />
                                </div>
                              )}

                              <div className="rev-o-antipodes__item">
                                <div className="c-app-showcase__toggles">
                                  <Button
                                    className="c-app-showcase__hide"
                                    leftIcon={ICON_NAMES.HIDE}
                                    onClick={() => {
                                      hideColumn(column.id);
                                    }}
                                    variant={ACTION_VARIANTS.GHOST}
                                  />
                                </div>
                              </div>
                            </div>
                          </th>
                        );
                      }}
                    </Draggable>
                  ),
                )}
                {droppableProvided.placeholder}
              </tr>
            )}
          </Droppable>
        </DragDropContext>
      ))}
    </thead>
  );
};

export default TableHeader;
